/* PowerSamoray — quick add from product cards.

   Adds to the cart without a page reload, refreshes the cart, and flies a small copy of the
   product image along a curve into the cart icon.

   Fast tapping (several products in a row):
   - Taps are collected and sent together: while one request is on its way, every new tap joins
     the next one, so 6-7 quick taps become 1-2 requests instead of 6-7 requests racing each
     other on the same cart.
   - When the last request is back, the cart is refreshed ONCE, straight from that response (no
     extra reload of the cart drawer per product).
   - window.psCartSync counts every cart change still in progress (these adds, and removals in
     the cart drawer). Check out waits until it is 0, so every product is in the cart before the
     checkout page opens. */

(() => {
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const FLY_MS = 380;
  const STEPS = 24;
  const CHIP_SIZE = 64; // the flying copy is a small chip, not a full card image
  const LANDING_RATIO = 0.5; // it disappears at half the icon's size, so it reads as going in

  /* ---------- shared "cart busy" tracker ---------- */
  const sync =
    window.psCartSync ||
    (window.psCartSync = (() => {
      let pending = 0;
      const waiters = [];
      return {
        get pending() {
          return pending;
        },
        /** Counts a cart change until the given promise settles. */
        track(promise) {
          pending += 1;
          Promise.resolve(promise)
            .catch(() => {})
            .finally(() => {
              pending -= 1;
              if (pending === 0) {
                waiters.splice(0).forEach((resolve) => resolve());
                document.dispatchEvent(new CustomEvent('ps:cart-sync-idle'));
              }
            });
        },
        /** Resolves when no cart change is in progress. */
        whenIdle() {
          return pending === 0 ? Promise.resolve() : new Promise((resolve) => waiters.push(resolve));
        },
      };
    })());

  /* ---------- fly-to-cart animation ---------- */

  /** Quintic ease-out: leaves at full speed on the tap and decelerates hard into the cart. */
  const easeOut = (t) => 1 - Math.pow(1 - t, 5);

  /** Point on a quadratic Bezier from (0,0) to (endX, endY) via a control point. */
  function bezier(t, controlX, controlY, endX, endY) {
    const inverse = 1 - t;
    return {
      x: 2 * inverse * t * controlX + t * t * endX,
      y: 2 * inverse * t * controlY + t * t * endY,
    };
  }

  function flyToCart(button) {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const card = button.closest('.ps-variant-card-wrapper, .product-card, .ps-search__card');
    const image = card?.querySelector('img');
    const cartIcon = document.querySelector('cart-icon') || document.querySelector('.header-actions__cart-icon');

    if (!image || !cartIcon) return;

    // Measure the glyph, not the <cart-icon> element (it also wraps the count bubble).
    const glyph = cartIcon.querySelector('svg') || cartIcon;
    const from = image.getBoundingClientRect();
    const to = glyph.getBoundingClientRect();

    if (!from.width || !to.width) return;

    const size = Math.min(CHIP_SIZE, from.width);
    const startLeft = from.left + from.width / 2 - size / 2;
    const startTop = from.top + from.height / 2 - size / 2;

    const clone = image.cloneNode(true);
    clone.removeAttribute('loading');
    clone.style.cssText = `
      position: fixed;
      left: ${startLeft}px;
      top: ${startTop}px;
      width: ${size}px;
      height: ${size}px;
      object-fit: cover;
      border-radius: 10px;
      pointer-events: none;
      z-index: 2147483000;
      will-change: transform, opacity;
    `;

    document.body.appendChild(clone);

    const glyphSize = Math.min(to.width, to.height);
    const endScale = Math.max(glyphSize * LANDING_RATIO, 8) / size;

    const endX = to.left + to.width / 2 - (startLeft + size / 2);
    const endY = to.top + to.height / 2 - (startTop + size / 2);

    const lift = Math.min(120, Math.max(40, Math.abs(endX) * 0.3));
    const controlX = endX * 0.25;
    const controlY = endY - lift;

    const keyframes = [];

    for (let step = 0; step <= STEPS; step += 1) {
      const raw = step / STEPS;
      const progress = easeOut(raw);
      const point = bezier(progress, controlX, controlY, endX, endY);
      const scale = 1 + (endScale - 1) * progress;

      keyframes.push({
        offset: raw,
        transform: `translate3d(${point.x}px, ${point.y}px, 0) scale(${scale})`,
        opacity: progress < 0.85 ? 1 : 1 - (progress - 0.85) * 4.5,
      });
    }

    const animation = clone.animate(keyframes, { duration: FLY_MS, easing: 'linear', fill: 'both' });
    animation.finished.then(
      () => clone.remove(),
      () => clone.remove()
    );
  }

  /* ---------- cart requests ---------- */

  /** Section ids of the cart drawer / cart page, so the add response carries their fresh HTML. */
  function cartSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((element) => {
      if (element instanceof HTMLElement && element.dataset.sectionId) ids.add(element.dataset.sectionId);
    });
    return Array.from(ids);
  }

  async function postAdd(items) {
    const sectionIds = cartSectionIds();
    const payload = { items };
    if (sectionIds.length) {
      payload.sections = sectionIds.join(',');
      payload.sections_url = window.location.pathname;
    }

    const response = await fetch(`${ROOT}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(data?.description || 'cart/add failed');
      error.status = response.status;
      throw error;
    }
    return data;
  }

  /** Tell the theme the cart changed, once, with the item count and the fresh cart HTML. */
  async function notifyCartUpdated(sections) {
    try {
      const response = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;

      const cart = await response.json();

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: 'ps-quick-add',
            data: {
              source: 'ps-quick-add',
              itemCount: cart.item_count,
              ...(sections ? { sections } : {}),
            },
          },
        })
      );
    } catch (error) {
      /* the items are in the cart either way; the bubble catches up on next load */
    }
  }

  const feedbackTimers = new WeakMap();
  function markButtons(buttons, className) {
    buttons.forEach((button) => {
      button.classList.remove('is-loading');
      clearTimeout(feedbackTimers.get(button));
      button.classList.remove('is-added', 'is-error');
      button.classList.add(className);
      const original = button.dataset.originalLabel || button.getAttribute('aria-label') || '';
      button.dataset.originalLabel = original;
      const label = className === 'is-added' ? button.dataset.addedLabel : button.dataset.errorLabel;
      if (label) button.setAttribute('aria-label', label);
      feedbackTimers.set(button, setTimeout(() => {
        button.classList.remove(className);
        button.setAttribute('aria-label', original);
      }, 1600));
    });
  }

  /** Taps waiting for the next request: variantId -> { quantity, buttons }. */
  let batch = new Map();
  let flushing = false;

  async function sendBatch(current) {
    const entries = Array.from(current.entries());
    const items = entries.map(([id, entry]) => ({ id: Number(id), quantity: entry.quantity }));

    try {
      const data = await postAdd(items);
      entries.forEach(([, entry]) => markButtons(entry.buttons, 'is-added'));
      return data.sections || null;
    } catch (error) {
      if (entries.length === 1) {
        markButtons(entries[0][1].buttons, 'is-error');
        return null;
      }
      /* One product could not be added (e.g. sold out) and Shopify rejected the whole request:
         add them one by one so all the others still go in. */
      let sections = null;
      for (const [id, entry] of entries) {
        try {
          const data = await postAdd([{ id: Number(id), quantity: entry.quantity }]);
          sections = data.sections || sections;
          markButtons(entry.buttons, 'is-added');
        } catch (_) {
          markButtons(entry.buttons, 'is-error');
        }
      }
      return sections;
    }
  }

  async function flush() {
    flushing = true;
    let sections = null;
    try {
      while (batch.size) {
        const current = batch;
        batch = new Map();
        sections = (await sendBatch(current)) || sections;
      }
      await notifyCartUpdated(sections);
    } finally {
      flushing = false;
      // A tap can arrive while the final cart refresh is awaiting Shopify.
      if (batch.size) sync.track(flush());
    }
  }

  function queueAdd(button) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const entry = batch.get(variantId) || { quantity: 0, buttons: [] };
    entry.quantity += 1;
    if (!entry.buttons.includes(button)) entry.buttons.push(button);
    batch.set(variantId, entry);

    const card = button.closest('.ps-variant-card-wrapper, .product-card, .ps-search__card, .resource-card');
    const image = card?.querySelector('img');
    const title = card?.querySelector(
      '.ps-available-variant-card__title, .product-card__title, .ps-search__title, .resource-card__title'
    )?.textContent?.trim() || image?.alt || button.getAttribute('aria-label') || '';
    const price = card?.querySelector(
      '.ps-available-variant-card__price > span:not(.ps-available-variant-card__compare), .price'
    )?.textContent?.trim() || '';
    document.dispatchEvent(new CustomEvent('ps:cart-pending-add', {
      detail: { variantId, title, image: image?.currentSrc || image?.src || '', price },
    }));
    document.dispatchEvent(new CustomEvent('ps:cart-count-adjust', { detail: { delta: 1 } }));

    // Start the flight on the tap, not after the network round trip.
    flyToCart(button);

    if (!flushing) sync.track(flush());
  }

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('[data-ps-quick-add]');
      if (!button) return;

      event.preventDefault();
      event.stopPropagation();
      queueAdd(button);
    },
    true
  );

  /* ---------- check out waits for the cart ---------- */

  const CHECKOUT_SELECTOR = 'button[name="checkout"], input[name="checkout"], a[href*="/checkout"], [data-ps-checkout]';

  function holdUntilIdle(element, proceed) {
    element.setAttribute('aria-busy', 'true');
    element.style.setProperty('opacity', '0.7');
    element.style.setProperty('pointer-events', 'none');
    sync.whenIdle().then(() => {
      element.removeAttribute('aria-busy');
      element.style.removeProperty('opacity');
      element.style.removeProperty('pointer-events');
      proceed();
    });
  }

  document.addEventListener(
    'click',
    (event) => {
      if (sync.pending === 0) return;
      const element = event.target instanceof Element ? event.target.closest(CHECKOUT_SELECTOR) : null;
      if (!element) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      holdUntilIdle(element, () => {
        if (element instanceof HTMLAnchorElement) {
          window.location.href = element.href;
        } else if ((element instanceof HTMLButtonElement || element instanceof HTMLInputElement) && element.form) {
          element.form.requestSubmit(element);
        } else {
          element.click();
        }
      });
    },
    true
  );

  /* Safety net for a checkout submitted without a click (keyboard, script). */
  document.addEventListener(
    'submit',
    (event) => {
      if (sync.pending === 0) return;
      const submitter = event.submitter;
      const form = event.target;
      const isCheckout =
        (submitter && submitter.getAttribute('name') === 'checkout') ||
        (form instanceof HTMLFormElement && /\/checkout/.test(form.getAttribute('action') || ''));
      if (!isCheckout || !(form instanceof HTMLFormElement)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      holdUntilIdle(submitter || form, () => form.requestSubmit(submitter || undefined));
    },
    true
  );
})();
